import {
  Bug,
  Building2,
  Calculator,
  FileSpreadsheet,
  LifeBuoy,
  Mail,
  CalendarDays,
  CalendarRange,
  Compass,
  HardHat,
  Inbox,
  ListChecks,
  Map as MapIcone,
  Receipt,
  ScrollText,
  ShieldCheck,
  UserCog,
  Users,
  Workflow,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { Acces } from '@/shared/auth/types';

export interface EntreeNavigation {
  id: string;
  libelle: string;
  chemin: string;
  icone: LucideIcon;
  roles: readonly Acces[];
  /** Réservé au CEO : les administrateurs ne le voient pas. */
  ceoSeul?: true;
  /** Ce que l'écran fera, affiché tant qu'il n'est pas livré. */
  description: string;
  /** Sprint qui livre l'écran ; absent quand il est déjà là. */
  prevuAu?: string;
  /** Mots qui mènent aussi à l'écran dans la recherche. */
  motsCles?: readonly string[];
}

export interface GroupeNavigation {
  libelle: string;
  entrees: readonly EntreeNavigation[];
}

const BUREAU = ['bureau'] as const;
const PARTENAIRE = ['partenaire'] as const;
/* L'assistant du bureau (db/030-niveaux-bureau.sql) : la saisie des fiches. */
const SAISIE = ['bureau', 'assistant'] as const;
const TOUS = ['bureau', 'assistant', 'partenaire'] as const;

export const NAVIGATION: readonly GroupeNavigation[] = [
  {
    libelle: 'Opérationnel',
    entrees: [
      {
        id: 'a-traiter',
        libelle: 'À traiter',
        chemin: '/a-traiter',
        icone: Inbox,
        roles: BUREAU,
        ceoSeul: true,
        description:
          'Les étapes envoyées par les partenaires, et les tickets SAV résolus, à valider.',
        motsCles: ['priorités', 'validations', 'contrôle', 'étapes envoyées'],
      },
      {
        id: 'chantiers',
        libelle: 'Chantiers',
        chemin: '/chantiers',
        icone: HardHat,
        roles: BUREAU,
        description: 'Chaque séchoir solaire signé, suivi de la livraison à la réception.',
        motsCles: ['séchoirs', 'installations', 'étapes'],
      },
      {
        id: 'etudes',
        libelle: 'Études',
        chemin: '/etudes',
        icone: Calculator,
        roles: BUREAU,
        description:
          'Les questionnaires de visite technique déposés par les partenaires, à importer pour générer l’étude en PDF.',
        motsCles: ['étude technique', 'pré-étude', 'relevé', 'questionnaire', 'pdf', 'visite'],
      },
      {
        id: 'planning',
        libelle: 'Planning',
        chemin: '/planning',
        icone: CalendarRange,
        roles: BUREAU,
        description: 'Une ligne par partenaire, une barre par étape, les conflits signalés.',
        motsCles: ['calendrier', 'agenda', 'interventions'],
      },
      {
        id: 'carte',
        libelle: 'Carte',
        chemin: '/carte',
        icone: MapIcone,
        roles: BUREAU,
        description:
          'Les chantiers sur la carte, en France, en Corse et outre-mer, et les zones sans partenaire.',
        motsCles: ['géographie', 'localisation', 'itinéraire', 'couverture', 'outre-mer'],
      },
      {
        id: 'sav',
        libelle: 'SAV',
        chemin: '/sav',
        icone: Wrench,
        roles: BUREAU,
        description:
          'Les tickets rattachés à un chantier, affectés et planifiés comme une intervention.',
        motsCles: ['service après-vente', 'panne', 'ticket'],
      },
      {
        id: 'guide',
        libelle: 'Comment ça marche',
        chemin: '/comment-ca-marche',
        icone: Compass,
        roles: PARTENAIRE,
        description:
          'Une étape du début à la fin : démarrer, les preuves, l’envoi, la réponse du bureau.',
        motsCles: ['guide', 'tutoriel', 'démarrer', 'mode d’emploi'],
      },
      {
        id: 'mes-etapes',
        libelle: 'Mes étapes',
        chemin: '/mes-etapes',
        icone: ListChecks,
        roles: PARTENAIRE,
        description:
          'Vos étapes du jour, à venir et à corriger, avec leurs photos et leur validation.',
        motsCles: ['interventions', 'chantiers'],
      },
      {
        id: 'mon-agenda',
        libelle: 'Mon agenda',
        chemin: '/mon-agenda',
        icone: CalendarDays,
        roles: PARTENAIRE,
        description:
          'Votre emploi du temps, semaine par semaine, aux dates que vous avez choisies.',
        motsCles: ['planning', 'emploi du temps', 'calendrier', 'semaine'],
      },
      {
        id: 'ma-carte',
        libelle: 'Ma carte',
        chemin: '/ma-carte',
        icone: MapIcone,
        roles: PARTENAIRE,
        description: 'Vos chantiers sur la carte, et l’itinéraire jusqu’au site.',
        motsCles: ['carte', 'itinéraire', 'adresse', 'gps', 'localisation'],
      },
      {
        id: 'tickets-sav',
        libelle: 'Tickets SAV',
        chemin: '/tickets-sav',
        icone: Wrench,
        roles: PARTENAIRE,
        description:
          'Signaler une panne sur un de vos chantiers : le système, la pièce, une photo, un commentaire.',
        motsCles: ['sav', 'panne', 'ticket', 'défaut', 'pièce', 'garantie'],
      },
      {
        id: 'mes-documents',
        libelle: 'Mes documents',
        chemin: '/mes-documents',
        icone: ShieldCheck,
        roles: PARTENAIRE,
        description: 'Kbis, décennale, attestation URSSAF : vos documents et leurs échéances.',
        motsCles: ['conformité', 'urssaf', 'décennale', 'kbis'],
      },
    ],
  },
  {
    libelle: 'Référentiels',
    entrees: [
      {
        id: 'entreprises',
        libelle: 'Entreprises',
        chemin: '/entreprises',
        icone: Building2,
        roles: SAISIE,
        description: 'Les clients : sites d’installation, contacts, devis signés.',
        motsCles: ['clients', 'exploitations'],
      },
      {
        id: 'partenaires',
        libelle: 'Partenaires',
        chemin: '/partenaires',
        icone: Users,
        roles: SAISIE,
        description: 'Les installateurs : étapes réalisées, zones, documents de conformité.',
        motsCles: ['sous-traitants', 'installateurs', 'équipes'],
      },
    ],
  },
  {
    libelle: 'Administration',
    entrees: [
      {
        id: 'utilisateurs',
        libelle: 'Utilisateurs',
        chemin: '/administration/utilisateurs',
        icone: UserCog,
        roles: BUREAU,
        description: 'Les comptes, leur rôle et leur rattachement.',
        motsCles: ['comptes', 'rôles', 'accès'],
      },
      {
        id: 'chaine',
        libelle: 'Chaîne d’étapes',
        chemin: '/administration/chaine',
        icone: Workflow,
        roles: BUREAU,
        description: 'Les preuves que chaque étape exige pour être validée.',
        motsCles: ['modèle', 'preuves', 'exigences', 'photos'],
      },
      {
        id: 'journal',
        libelle: 'Journal des actions',
        chemin: '/administration/journal',
        icone: ScrollText,
        roles: BUREAU,
        description: 'Qui a créé, modifié ou supprimé quoi, et quand.',
        motsCles: ['historique', 'audit', 'traces'],
      },
      {
        id: 'courriels',
        libelle: 'E-mails envoyés',
        chemin: '/administration/courriels',
        icone: Mail,
        roles: BUREAU,
        description:
          'Les e-mails partis de notifications@vla-logistique.com, et s’ils sont bien arrivés.',
        motsCles: ['courriels', 'mails', 'notifications', 'historique', 'échanges'],
      },
      {
        id: 'import',
        libelle: 'Import',
        chemin: '/administration/import',
        icone: FileSpreadsheet,
        roles: BUREAU,
        description: 'Reprendre les clients et les partenaires d’un tableau Excel.',
        motsCles: ['excel', 'csv', 'reprise', 'clients', 'partenaires'],
      },
      {
        id: 'factures',
        libelle: 'Mes factures',
        chemin: '/administration/factures',
        icone: Receipt,
        roles: BUREAU,
        ceoSeul: true,
        description:
          'Votre facture du mois à VLA Énergie (forfait et primes par chantier) et l’historique de vos factures.',
        motsCles: ['facture', 'primes', 'comptabilité', 'forfait', 'encaissements'],
      },
      {
        id: 'incidents',
        libelle: 'Incidents',
        chemin: '/administration/incidents',
        icone: Bug,
        roles: BUREAU,
        description:
          'Les erreurs rencontrées dans l’application, chez le bureau comme chez les partenaires. Effacez-les une fois traitées.',
        motsCles: ['erreurs', 'bugs', 'pannes'],
      },
    ],
  },
  {
    libelle: 'Assistance',
    entrees: [
      {
        id: 'aide',
        libelle: 'Aide',
        chemin: '/aide',
        icone: LifeBuoy,
        roles: TOUS,
        description:
          'Le guide de l’appli terrain : installer, faire une étape, travailler sans réseau.',
        motsCles: ['guide', 'mode d’emploi', 'installer', 'téléphone'],
      },
    ],
  },
];

/** Ce rôle (et le CEO, pour ce qui lui est réservé) peut-il ouvrir cette entrée ? */
export function ouvrable(entree: EntreeNavigation, role: Acces, ceo: boolean): boolean {
  return entree.roles.includes(role) && (!entree.ceoSeul || ceo);
}

/** La navigation qu'un rôle voit : les groupes vides disparaissent. */
export function navigationPour(role: Acces, ceo = false): GroupeNavigation[] {
  return NAVIGATION.map((groupe) => ({
    libelle: groupe.libelle,
    entrees: groupe.entrees.filter((e) => ouvrable(e, role, ceo)),
  })).filter((groupe) => groupe.entrees.length > 0);
}

export function toutesLesEntrees(): EntreeNavigation[] {
  return NAVIGATION.flatMap((g) => g.entrees);
}

/** L'écran d'arrivée après connexion. */
export function accueilPour(role: Acces, ceo = false): string {
  /* Le CEO arrive sur ce qui attend sa validation ; l'administrateur, sur les
     chantiers ; l'assistant, sur les fiches qu'il saisit ; le partenaire, sur
     ses interventions. */
  if (role === 'assistant') return '/entreprises';
  if (role === 'bureau') return ceo ? '/a-traiter' : '/chantiers';
  return '/mes-etapes';
}

/** L'entrée de navigation qui porte ce chemin, ou qui en est le parent le plus proche. */
export function entreePourChemin(chemin: string): EntreeNavigation | undefined {
  return toutesLesEntrees()
    .filter((e) => chemin === e.chemin || chemin.startsWith(`${e.chemin}/`))
    .sort((a, b) => b.chemin.length - a.chemin.length)[0];
}

export function groupeDe(entree: EntreeNavigation): GroupeNavigation | undefined {
  return NAVIGATION.find((g) => g.entrees.includes(entree));
}
